import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import { adminReportService, CarrierTotal } from "../../services/admin.report.service";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faFileExcel,
    faSearch,
    faCircleNotch,
    faCalendarAlt,
    faInfoCircle,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/AdminRatesReportPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";

const AdminRatesReportPage: React.FC = () => {
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    // Check permissions
    useEffect(() => {
        if (!permissionCheckerService.checkPermission(adminData, "REPORTS", "RATES")) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate]);

    // Form states
    const formatDate = (date: Date) => {
        return date.toISOString().split("T")[0];
    };

    const getInitialDates = () => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
            start: formatDate(startOfMonth),
            end: formatDate(today)
        };
    };

    const initialDates = getInitialDates();
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [detail, setDetail] = useState(false);

    // Results states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<CarrierTotal[] | null>(null);
    const [isDetailedReport, setIsDetailedReport] = useState(false);

    const handleSetDateRange = async (rangeType: "6months" | "3months" | "prevMonth" | "thisMonth") => {
        const today = new Date();
        let start = new Date();
        let end = new Date();

        switch (rangeType) {
            case "6months":
                start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
                end = today;
                break;
            case "3months":
                start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
                end = today;
                break;
            case "prevMonth":
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                end = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "thisMonth":
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = today;
                break;
        }

        const formattedStart = formatDate(start);
        const formattedEnd = formatDate(end);

        setStartDate(formattedStart);
        setEndDate(formattedEnd);

        setLoading(true);
        setReportData(null);
        setError(null);

        try {
            const data = await adminReportService.getRatesReport({
                startDate: `${formattedStart}T00:00:00Z`,
                endDate: `${formattedEnd}T00:00:00Z`,
                detail
            });

            if (data.statusCode === 200 && data.data) {
                setReportData(data.data);
                setIsDetailedReport(detail);
            } else {
                throw new Error(data.message || "Error desconocido al obtener el reporte.");
            }
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Error al conectar con la API.");
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setReportData(null);
        setError(null);

        try {
            const data = await adminReportService.getRatesReport({
                startDate: `${startDate}T00:00:00Z`,
                endDate: `${endDate}T00:00:00Z`,
                detail
            });

            if (data.statusCode === 200 && data.data) {
                setReportData(data.data);
                setIsDetailedReport(detail);
            } else {
                throw new Error(data.message || "Error desconocido al obtener el reporte.");
            }
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Error al conectar con la API.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            const blob = await adminReportService.downloadRatesReportExcel({
                startDate: `${startDate}T00:00:00Z`,
                endDate: `${endDate}T00:00:00Z`,
                detail
            });

            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            const reportType = detail ? "Detallado" : "General";
            link.download = `Reporte_Couriers_${reportType}_${startDate.replace(/-/g, "")}_${endDate.replace(/-/g, "")}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err: any) {
            console.error("Error downloading file:", err);
            setError("Hubo un error al descargar el reporte en Excel.");
        }
    };

    // Calculate totals across all carriers
    const reportDataArray = Array.isArray(reportData) ? reportData : [];
    const totalCarrierAll = reportDataArray.reduce((sum, item) => sum + item.totalCarrier, 0);
    const avgTasaAll = reportDataArray.length > 0 ? reportDataArray.reduce((sum, item) => sum + item.tasa, 0) / reportDataArray.length : 0;
    const totalPagarAll = reportDataArray.reduce((sum, item) => sum + item.pagarCarrier, 0);
    const totalProfitAll = reportDataArray.reduce((sum, item) => sum + item.totalGanancia, 0);
    const totalOrdersAll = reportDataArray.reduce((sum, item) => sum + item.orderCount, 0);

    const dataToRender = Array.isArray(reportData)
        ? [
              ...reportData,
              {
                  carrierId: -999,
                  carrierName: "Total General",
                  orderCount: totalOrdersAll,
                  totalCarrier: totalCarrierAll,
                  tasa: avgTasaAll,
                  pagarCarrier: totalPagarAll,
                  totalGanancia: totalProfitAll
              }
          ]
        : [];

    const OrderDetailView: React.FC<{ data: CarrierTotal }> = ({ data }) => {
        if (!data.orders || data.orders.length === 0) return null;
        return (
            <div style={{ padding: "8px 24px 12px 24px", background: "#f8fafc", borderTop: "1px dashed #e2e8f0" }}>
                <h5 style={{ fontSize: "13px", fontWeight: "700", color: "#475569", marginBottom: "8px" }}>
                    Órdenes Detalladas
                </h5>
                <div style={{ maxHeight: "200px", overflowY: "auto", paddingRight: "6px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px", paddingBottom: "4px" }}>
                        {data.orders.map((order) => (
                            <div 
                                key={order.idOrder} 
                                onClick={() => window.open(`/admin/order/${order.idOrder}`, "_blank")}
                                style={{ 
                                    padding: "8px 12px", 
                                    background: "white", 
                                    border: "1px solid #e2e8f0", 
                                    borderRadius: "6px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "2px",
                                    cursor: "pointer"
                                }}
                                title="Ver detalle de orden"
                            >
                                <span style={{ fontSize: "11px", color: "var(--brand-black, #0a0a0a)", fontWeight: "700" }}>
                                    ID Orden: {order.idOrder}
                                </span>
                                <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>
                                    {order.tracking}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const columns: TableColumn<CarrierTotal>[] = [
        {
            name: "Courier",
            selector: (row) => row.carrierName,
            sortable: true,
            cell: (row) => (
                <span style={row.carrierId === -999 ? { fontWeight: "bold", textTransform: "uppercase" } : undefined}>
                    {row.carrierName}
                </span>
            )
        },
        {
            name: "Cantidad de Órdenes",
            selector: (row) => row.orderCount,
            sortable: true,
            center: true,
            cell: (row) => (
                <span style={row.carrierId === -999 ? { fontWeight: "bold" } : undefined}>
                    {row.orderCount}
                </span>
            )
        },
        {
            name: "Total Courier",
            selector: (row) => row.totalCarrier,
            sortable: true,
            right: true,
            cell: (row) => (
                <span style={row.carrierId === -999 ? { fontWeight: "bold" } : undefined}>
                    ${row.totalCarrier.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            )
        },
        {
            name: "Tasa Promedio",
            selector: (row) => row.tasa,
            sortable: true,
            right: true,
            cell: (row) => (
                <span style={row.carrierId === -999 ? { fontWeight: "bold" } : undefined}>
                    ${row.tasa.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            )
        },
        {
            name: "Pagar Courier",
            selector: (row) => row.pagarCarrier,
            sortable: true,
            right: true,
            cell: (row) => (
                <span style={row.carrierId === -999 ? { fontWeight: "bold" } : undefined}>
                    ${row.pagarCarrier.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            )
        },
        {
            name: "Total Ganancia",
            selector: (row) => row.totalGanancia,
            sortable: true,
            right: true,
            cell: (row) => (
                <span style={row.carrierId === -999 ? { fontWeight: "bold" } : { color: "#10b981", fontWeight: "700" }}>
                    ${row.totalGanancia.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            )
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "60px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
        expanderRow: {
            style: {
                backgroundColor: "#f8fafc",
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.adminReportContainer}>
                <DashboardCard
                    icon={faFileExcel}
                    title="Reporte de Tarifas y Ganancias"
                    description="Consulta y descarga el consolidado de tarifas, importes a pagar y ganancias estimadas por courier."
                    actions={
                        reportData && (
                            <button 
                                onClick={handleDownloadExcel} 
                                className={styles.btnDownload}
                                title="Descargar Reporte en Excel"
                            >
                                <FontAwesomeIcon icon={faFileExcel} /> Descargar Excel
                            </button>
                        )
                    }
                />

                <div className={styles.tableWrapper}>
                    <form className={styles.tableActions} onSubmit={handleGenerateReport}>
                        <div className={styles.filterSection}>
                            <div className={styles.filterGroup}>
                                <label>
                                    <FontAwesomeIcon icon={faCalendarAlt} /> Fecha Inicio
                                </label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className={styles.filterInput}
                                    required
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>
                                    <FontAwesomeIcon icon={faCalendarAlt} /> Fecha Fin
                                </label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className={styles.filterInput}
                                    required
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>Formato de Reporte</label>
                                <select
                                    className={styles.filterSelect}
                                    value={detail ? "true" : "false"}
                                    onChange={(e) => setDetail(e.target.value === "true")}
                                >
                                    <option value="false">General</option>
                                    <option value="true">Detallado</option>
                                </select>
                            </div>
                            <div className={`${styles.filterGroup} ${styles.buttonGroup}`}>
                                <label>&nbsp;</label>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={btnStyles.primary}
                                >
                                    {loading ? (
                                        <>
                                            <FontAwesomeIcon icon={faCircleNotch} spin /> Generando...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSearch} /> Generar Reporte
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className={styles.quickRangesRow}>
                            <span>Atajos de Fecha:</span>
                            <button type="button" onClick={() => handleSetDateRange("thisMonth")} className={styles.btnQuickRange}>Este Mes</button>
                            <button type="button" onClick={() => handleSetDateRange("prevMonth")} className={styles.btnQuickRange}>Mes Anterior</button>
                            <button type="button" onClick={() => handleSetDateRange("3months")} className={styles.btnQuickRange}>Últimos 3 Meses</button>
                            <button type="button" onClick={() => handleSetDateRange("6months")} className={styles.btnQuickRange}>Últimos 6 Meses</button>
                        </div>
                    </form>

                    {error ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                            <h3>Error</h3>
                            <p>{error}</p>
                        </div>
                    ) : loading ? (
                        <div className={styles.loadingContainer}>
                            <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                            <p>Cargando información del reporte. Esto puede demorar unos segundos...</p>
                        </div>
                    ) : Array.isArray(reportData) ? (
                        <DataTable
                            columns={columns}
                            data={dataToRender}
                            pagination
                            progressPending={loading}
                            customStyles={customStyles}
                            responsive
                            highlightOnHover
                            noDataComponent={
                                <div className={styles.emptyContainer}>
                                    <FontAwesomeIcon icon={faInfoCircle} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                                    <p>No se encontraron registros para este rango de fechas.</p>
                                </div>
                            }
                            expandableRows={isDetailedReport}
                            expandableRowsComponent={OrderDetailView}
                            expandableRowDisabled={(row) => row.carrierId === -999 || !row.orders || row.orders.length === 0}
                        />
                    ) : (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faInfoCircle} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <p>Selecciona un rango de fechas y presiona "Generar Reporte" para visualizar los datos.</p>
                        </div>
                    )}
                </div>
            </div>
        </AdminDashboardLayout>
    );
};

export default AdminRatesReportPage;
